// src/screens/AboutScreen.jsx

import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import {
  ArrowUpRight,
  Calculator,
  ShieldCheck,
  Monitor,
} from "lucide-react";

import SolContDigital from "../components/SolContDigital";

/* ======================================================
   IDIOMA COMPARTIDO

   Mismo contrato que MainScreen:
   - Almacenamiento: solcont-language
   - Evento: solcont:language-change

   También admite:
   <AboutScreen language="fr" />
====================================================== */

const LANGUAGE_STORAGE_KEY = "solcont-language";
const LANGUAGE_CHANGE_EVENT = "solcont:language-change";

const SUPPORTED_LANGUAGES = ["es", "en", "fr"];

const OG_LOCALES = {
  es: "es_MX",
  en: "en_US",
  fr: "fr_CA",
};

function normalizeLanguage(value) {
  if (typeof value !== "string") return null;

  const normalized = value
    .trim()
    .toLowerCase()
    .split(/[-_]/)[0];

  return SUPPORTED_LANGUAGES.includes(normalized)
    ? normalized
    : null;
}

function readStoredLanguage() {
  if (typeof window === "undefined") return "es";

  try {
    return (
      normalizeLanguage(
        window.localStorage.getItem(LANGUAGE_STORAGE_KEY)
      ) || "es"
    );
  } catch {
    return "es";
  }
}

function useAboutLanguage(controlledLanguage) {
  const [storedLanguage, setStoredLanguage] =
    useState(readStoredLanguage);

  useEffect(() => {
    const handleLanguageChange = (event) => {
      const value =
        typeof event.detail === "string"
          ? event.detail
          : event.detail?.language;

      const nextLanguage = normalizeLanguage(value);

      if (!nextLanguage) return;

      setStoredLanguage(nextLanguage);

      try {
        window.localStorage.setItem(
          LANGUAGE_STORAGE_KEY,
          nextLanguage
        );
      } catch {
        // El cambio funciona aunque el almacenamiento no esté disponible.
      }
    };

    const handleStorageChange = (event) => {
      if (
        event.key === LANGUAGE_STORAGE_KEY ||
        event.key === null
      ) {
        setStoredLanguage(readStoredLanguage());
      }
    };

    window.addEventListener(
      LANGUAGE_CHANGE_EVENT,
      handleLanguageChange
    );

    window.addEventListener(
      "storage",
      handleStorageChange
    );

    return () => {
      window.removeEventListener(
        LANGUAGE_CHANGE_EVENT,
        handleLanguageChange
      );

      window.removeEventListener(
        "storage",
        handleStorageChange
      );
    };
  }, []);

  return (
    normalizeLanguage(controlledLanguage) ||
    storedLanguage
  );
}

/* ======================================================
   IDENTIDAD VISUAL
====================================================== */

const DISPLAY_STYLE = {
  fontFamily:
    '"Arial Narrow", "Roboto Condensed", Arial, sans-serif',
  fontStretch: "condensed",
};

const GRID_STYLE = {
  backgroundColor: "#1648EE",
  backgroundImage: `
    linear-gradient(
      rgba(255,255,255,0.065) 1px,
      transparent 1px
    ),
    linear-gradient(
      90deg,
      rgba(255,255,255,0.065) 1px,
      transparent 1px
    )
  `,
  backgroundSize: "64px 64px",
};


/* ======================================================
   ESCALA RESPONSIVA — MISMO CRITERIO QUE MAINSCREEN

   App ya compensa el navbar de 4rem con pt-16.
   No añadimos otro espacio superior ni alturas de pantalla.
   Sólo los elementos sab-* de AboutScreen reciben estos estilos;
   SolContDigital conserva sus propios estilos y funcionamiento.
====================================================== */

const RESPONSIVE_STYLES = `
  .solcont-about {
    --sab-header-offset: 4rem;
    --sab-section-space: clamp(2.5rem, 6vh, 5rem);
    --sab-hero-space: clamp(1.75rem, 5vh, 4rem);
    --sab-stack: clamp(1rem, 2.5vh, 1.75rem);
    --sab-gap: clamp(1.5rem, 3vw, 3.5rem);
    --sab-panel: clamp(1.25rem, 2.2vw, 2rem);
    --sab-image-height: clamp(13rem, 33vh, 22rem);
  }
  .solcont-about .sab-container {
    box-sizing: border-box;
    width: 100%;
    max-width: 75rem;
    margin-inline: auto;
    padding-inline: clamp(1rem, 3vw, 2.5rem);
  }
  .solcont-about .sab-section { padding-block: var(--sab-section-space); }
  .solcont-about .sab-anchor {
    scroll-margin-top: calc(var(--sab-header-offset) + 1rem);
  }
  .solcont-about .sab-split,
  .solcont-about .sab-heading {
    grid-template-columns: minmax(0, 1fr);
    gap: var(--sab-gap);
  }
  .solcont-about .sab-hero { padding-block: var(--sab-hero-space); }
  .solcont-about .sab-accent { margin-top: 1rem; }
  .solcont-about .sab-spaced { margin-top: var(--sab-stack); }
  .solcont-about .sab-hero-title {
    margin-top: var(--sab-stack);
    font-size: clamp(2rem, 7vw, 3.5rem);
    line-height: 1.02;
    text-wrap: balance;
    overflow-wrap: anywhere;
  }
  .solcont-about .sab-title {
    font-size: clamp(1.875rem, 3.6vw, 3.125rem);
    line-height: 1.06;
    text-wrap: balance;
    overflow-wrap: anywhere;
  }
  .solcont-about .sab-copy {
    margin-top: var(--sab-stack);
    font-size: 1rem;
    line-height: 1.6;
  }
  .solcont-about .sab-paragraphs {
    margin-top: var(--sab-stack);
    font-size: 1rem;
    line-height: 1.6;
  }
  .solcont-about .sab-paragraphs > p + p { margin-top: 1rem; }
  .solcont-about .sab-button {
    min-height: 2.75rem;
    max-width: 100%;
    padding: 0.75rem 1.25rem;
    line-height: 1.4;
    text-align: center;
    white-space: normal;
  }
  .solcont-about .sab-panel { padding: var(--sab-panel); }
  .solcont-about .sab-panel-list { margin-top: 1.25rem; }
  .solcont-about .sab-pillar {
    gap: 0.75rem;
    padding-block: 0.875rem;
  }
  .solcont-about .sab-pillar-icon { width: 2.25rem; height: 2.25rem; }
  .solcont-about .sab-pillar-title {
    font-size: clamp(1.125rem, 2.2vw, 1.625rem);
    line-height: 1.2;
    overflow-wrap: anywhere;
  }
  .solcont-about .sab-panel-note { margin-top: 1rem; padding: 1rem; }
  .solcont-about .sab-panel-note > p { line-height: 1.5; }
  .solcont-about .sab-image-label { padding: 1rem var(--sab-panel); }
  .solcont-about .sab-photo {
    display: block;
    width: 100%;
    height: auto;
    aspect-ratio: 8 / 5;
    object-fit: cover;
  }
  .solcont-about .sab-fallback {
    box-sizing: border-box;
    min-width: 0;
    aspect-ratio: 8 / 5;
    padding: var(--sab-panel);
  }
  .solcont-about .sab-fallback > div { min-width: 0; }
  .solcont-about .sab-fallback-title {
    margin-top: var(--sab-stack);
    font-size: clamp(1.5rem, 3vw, 2.5rem);
    line-height: 1.12;
    overflow-wrap: anywhere;
  }
  .solcont-about .sab-caption { margin-top: 0.75rem; line-height: 1.6; }
  .solcont-about .sab-cards { margin-top: var(--sab-gap); gap: 1.25rem; }
  .solcont-about .sab-purpose-header { padding-bottom: 1rem; }
  .solcont-about .sab-card-title {
    margin-top: var(--sab-stack);
    font-size: clamp(1.5rem, 2.5vw, 2rem);
    line-height: 1.12;
    text-wrap: balance;
    overflow-wrap: anywhere;
  }
  .solcont-about .sab-card-copy {
    margin-top: 1rem;
    font-size: 1rem;
    line-height: 1.6;
  }
  .solcont-about .sab-card-rule { padding-top: 1.25rem; }
  .solcont-about .sab-digital-content { margin-top: var(--sab-gap); }

  @supports (height: 100svh) {
    .solcont-about {
      --sab-section-space: clamp(2.5rem, 6svh, 5rem);
      --sab-hero-space: clamp(1.75rem, 5svh, 4rem);
      --sab-stack: clamp(1rem, 2.5svh, 1.75rem);
      --sab-image-height: clamp(13rem, 33svh, 22rem);
    }
  }

  @media (min-width: 60rem) {
    .solcont-about .sab-split { grid-template-columns: minmax(0, 1fr) minmax(0, 1fr); }
    .solcont-about .sab-hero { grid-template-columns: minmax(0, 1.25fr) minmax(0, 0.85fr); }
    .solcont-about .sab-essence { grid-template-columns: minmax(0, 0.95fr) minmax(0, 1.05fr); }
    .solcont-about .sab-heading { grid-template-columns: minmax(0, 0.65fr) minmax(0, 1.35fr); }
    .solcont-about .sab-hero-title {
      font-size: clamp(2.375rem, min(4.2vw, 7.2vh), 4.25rem);
    }
    .solcont-about .sab-title {
      font-size: clamp(1.875rem, min(3.6vw, 5.8vh), 3.125rem);
    }
    .solcont-about .sab-photo { height: var(--sab-image-height); aspect-ratio: auto; }
    .solcont-about .sab-fallback {
      min-height: var(--sab-image-height);
      aspect-ratio: auto;
    }
    @supports (height: 100svh) {
      .solcont-about .sab-hero-title {
        font-size: clamp(2.375rem, min(4.2vw, 7.2svh), 4.25rem);
      }
      .solcont-about .sab-title {
        font-size: clamp(1.875rem, min(3.6vw, 5.8svh), 3.125rem);
      }
    }
  }

  @media (min-width: 60rem) and (max-height: 46.25rem) {
    .solcont-about {
      --sab-section-space: 2rem;
      --sab-hero-space: 1.5rem;
      --sab-stack: 1rem;
      --sab-panel: 1.25rem;
    }
    .solcont-about .sab-hero-title {
      font-size: clamp(2.25rem, min(4.2vw, 6.8vh), 3.25rem);
    }
    .solcont-about .sab-panel-list { margin-top: 1rem; }
    .solcont-about .sab-pillar { padding-block: 0.625rem; }
    .solcont-about .sab-pillar-icon { width: 2rem; height: 2rem; }
    .solcont-about .sab-pillar-title { font-size: 1.375rem; }
    .solcont-about .sab-panel-note { margin-top: 0.75rem; padding: 0.75rem; }
  }

  @media (max-width: 39.999rem) {
    .solcont-about .sab-button { width: 100%; }
  }

  /* El contenido crece de forma natural en móvil y con zoom.
     No hay scale(), alturas fijas para textos ni scroll interno. */
`;

const PILLAR_ICONS = [
  Calculator,
  ShieldCheck,
  Monitor,
];

/* ======================================================
   TRADUCCIONES
====================================================== */

const CONTENT = {
  es: {
    seo: {
      title: "Quiénes somos | SolCont México",
      description:
        "Conoce SolCont México. Combinamos contabilidad, estrategia fiscal y tecnología para acompañar a personas, profesionistas y empresas en todo México.",
    },

    hero: {
      eyebrow: "SOLCONT / QUIÉNES SOMOS",
      title: ["Juntos hacemos", "el progreso."],
      description:
        "Combinamos estrategia fiscal, cumplimiento y tecnología para ofrecer soluciones que ayudan a nuestros clientes a avanzar con claridad.",
      cardLabel: "NUESTRA FORMA DE TRABAJAR",
      pillars: [
        "Estrategia",
        "Cumplimiento",
        "Tecnología",
      ],
      cardNote:
        "Una visión integral de tu operación.",
    },

    essence: {
      eyebrow: "NUESTRA ESENCIA",
      title: "Lo que nos distingue.",
      paragraphs: [
        "Unimos experiencia contable, estrategia fiscal y herramientas digitales para construir soluciones que acompañan la evolución de cada negocio.",
        "Creemos que la contabilidad moderna debe aportar claridad, generar confianza y ofrecer información útil para tomar decisiones con visión empresarial.",
      ],
      cta: "Conoce nuestros servicios",
      imageAlt: "Equipo SolCont trabajando en conjunto",
      imageLabel: "LAS PERSONAS DETRÁS DE LOS NÚMEROS",
      caption:
        "Criterio profesional y colaboración en cada etapa.",
      fallbackTitle:
        "Claridad. Confianza. Dirección.",
    },

    purpose: {
      eyebrow: "EL PROPÓSITO QUE NOS MUEVE",
      title: "Una dirección clara para avanzar juntos.",
      items: [
        {
          label: "MISIÓN",
          title: "Tranquilidad operativa y fiscal.",
          description:
            "Brindar tranquilidad operativa y fiscal mediante procesos estandarizados, evidencia disponible y comunicación clara que convierta la contabilidad en información útil para actuar.",
        },
        {
          label: "VISIÓN",
          title: "Ser una firma de referencia en México.",
          description:
            "Ser la firma de referencia para negocios que buscan cumplimiento confiable, eficiencia operativa y una dirección financiera precisa.",
        },
      ],
    },

    digital: {
      eyebrow: "SOLCONT / SOLUCIONES DIGITALES",
      title: "La tecnología también es parte de la solución.",
      description:
        "Integramos herramientas digitales y acompañamiento profesional para facilitar la operación y la comunicación con nuestros clientes.",
    },
  },

  en: {
    seo: {
      title: "About us | SolCont México",
      description:
        "Discover SolCont México. We combine accounting, tax strategy and technology to support individuals, professionals and businesses throughout Mexico.",
    },

    hero: {
      eyebrow: "SOLCONT / ABOUT US",
      title: ["Together,", "we move forward."],
      description:
        "We combine tax strategy, compliance and technology to provide solutions that help our clients move forward with clarity.",
      cardLabel: "HOW WE WORK",
      pillars: [
        "Strategy",
        "Compliance",
        "Technology",
      ],
      cardNote:
        "A comprehensive view of your operations.",
    },

    essence: {
      eyebrow: "WHAT DEFINES US",
      title: "What sets us apart.",
      paragraphs: [
        "We bring together accounting expertise, tax strategy and digital tools to develop solutions that evolve with each business.",
        "We believe modern accounting should provide clarity, build trust and deliver useful information for informed business decisions.",
      ],
      cta: "Explore our services",
      imageAlt: "The SolCont team working together",
      imageLabel: "THE PEOPLE BEHIND THE NUMBERS",
      caption:
        "Professional judgment and collaboration at every stage.",
      fallbackTitle:
        "Clarity. Trust. Direction.",
    },

    purpose: {
      eyebrow: "THE PURPOSE THAT GUIDES US",
      title: "A clear direction for moving forward together.",
      items: [
        {
          label: "MISSION",
          title: "Operational and tax peace of mind.",
          description:
            "To provide operational and tax peace of mind through standardized processes, accessible supporting documentation and clear communication that turns accounting into actionable information.",
        },
        {
          label: "VISION",
          title: "To become a trusted reference in Mexico.",
          description:
            "To be the go-to firm for businesses seeking reliable compliance, operational efficiency and clear financial direction.",
        },
      ],
    },

    digital: {
      eyebrow: "SOLCONT / DIGITAL SOLUTIONS",
      title: "Technology is part of the solution.",
      description:
        "We combine digital tools with professional support to simplify operations and communication with our clients.",
    },
  },

  fr: {
    seo: {
      title: "Qui sommes-nous ? | SolCont México",
      description:
        "Découvrez SolCont México. Nous associons comptabilité, stratégie fiscale et technologie pour accompagner les particuliers, les professionnels et les entreprises au Mexique.",
    },

    hero: {
      eyebrow: "SOLCONT / QUI SOMMES-NOUS",
      title: ["Ensemble,", "nous avançons."],
      description:
        "Nous associons stratégie fiscale, conformité et technologie pour proposer des solutions qui permettent à nos clients d’avancer avec clarté.",
      cardLabel: "NOTRE FAÇON DE TRAVAILLER",
      pillars: [
        "Stratégie",
        "Conformité",
        "Technologie",
      ],
      cardNote:
        "Une vision globale de votre activité.",
    },

    essence: {
      eyebrow: "NOTRE IDENTITÉ",
      title: "Ce qui nous distingue.",
      paragraphs: [
        "Nous réunissons expertise comptable, stratégie fiscale et outils numériques pour développer des solutions adaptées à l’évolution de chaque entreprise.",
        "Nous croyons qu’une comptabilité moderne doit apporter de la clarté, inspirer confiance et fournir des informations utiles pour prendre des décisions éclairées.",
      ],
      cta: "Découvrir nos services",
      imageAlt: "L’équipe SolCont travaillant ensemble",
      imageLabel: "LES PERSONNES DERRIÈRE LES CHIFFRES",
      caption:
        "Jugement professionnel et collaboration à chaque étape.",
      fallbackTitle:
        "Clarté. Confiance. Direction.",
    },

    purpose: {
      eyebrow: "LE BUT QUI NOUS GUIDE",
      title: "Une direction claire pour avancer ensemble.",
      items: [
        {
          label: "MISSION",
          title: "La tranquillité opérationnelle et fiscale.",
          description:
            "Apporter de la tranquillité sur les plans opérationnel et fiscal grâce à des processus structurés, des justificatifs accessibles et une communication claire qui transforme la comptabilité en informations utiles pour agir.",
        },
        {
          label: "VISION",
          title: "Devenir une référence au Mexique.",
          description:
            "Être le cabinet de référence pour les entreprises qui recherchent une conformité fiable, une efficacité opérationnelle et une orientation financière claire.",
        },
      ],
    },

    digital: {
      eyebrow: "SOLCONT / SOLUTIONS NUMÉRIQUES",
      title: "La technologie fait aussi partie de la solution.",
      description:
        "Nous associons outils numériques et accompagnement professionnel pour faciliter les opérations et la communication avec nos clients.",
    },
  },
};

/* ======================================================
   COMPONENTES VISUALES
====================================================== */

function AccentRule({ className = "" }) {
  return (
    <span
      aria-hidden="true"
      className={`sab-accent block h-1 w-16 rounded-full bg-[#F72585] ${className}`}
    />
  );
}

function Eyebrow({ children, light = false }) {
  return (
    <p
      className={[
        "text-[11px] font-bold uppercase leading-relaxed",
        "tracking-[0.16em] sm:text-xs",
        light ? "text-blue-100" : "text-[#1648EE]",
      ].join(" ")}
    >
      {children}
    </p>
  );
}

function BrandOrbits() {
  return (
    <div
      aria-hidden="true"
      className="pointer-events-none absolute inset-0 overflow-hidden"
    >
      <div className="absolute -right-64 -top-48 h-[560px] w-[560px] rounded-full border border-white/35 sm:-right-48 sm:h-[700px] sm:w-[700px]" />

      <div className="absolute -right-40 -top-16 h-[350px] w-[350px] rounded-full border border-white/35 sm:-right-24 sm:h-[450px] sm:w-[450px]" />

      <span className="absolute right-28 top-24 h-5 w-5 rounded-full bg-[#F72585] sm:right-64 sm:top-28 sm:h-6 sm:w-6" />
    </div>
  );
}

function TeamImage({ copy }) {
  const [imageError, setImageError] = useState(false);

  return (
    <figure className="min-w-0">
      <div className="overflow-hidden rounded-3xl border border-[#081B3E]/10 bg-white">
        <div className="sab-image-label flex items-center gap-4">
          <span
            aria-hidden="true"
            className="h-2.5 w-2.5 shrink-0 rounded-full bg-[#F72585]"
          />

          <span className="text-[10px] font-bold uppercase leading-5 tracking-[0.13em] text-[#1648EE]">
            {copy.imageLabel}
          </span>
        </div>

        {imageError ? (
          <div
            className="sab-fallback relative flex items-center overflow-hidden text-white"
            style={GRID_STYLE}
          >
            <BrandOrbits />

            <div className="relative z-10 max-w-sm">
              <AccentRule />

              <p
                style={DISPLAY_STYLE}
                className="sab-fallback-title font-black uppercase tracking-tight"
              >
                {copy.fallbackTitle}
              </p>
            </div>
          </div>
        ) : (
          <img
            src="/media/team.png"
            alt={copy.imageAlt}
            className="sab-photo"
            loading="lazy"
            decoding="async"
            onError={() => setImageError(true)}
          />
        )}
      </div>

      <figcaption className="sab-caption px-1 text-xs text-slate-500">
        {copy.caption}
      </figcaption>
    </figure>
  );
}

function PurposeCard({ index, label, title, description }) {
  return (
    <article className="sab-panel flex h-full min-w-0 flex-col rounded-3xl bg-[#F7F3E9] text-[#081B3E]">
      <div className="sab-purpose-header flex items-center justify-between gap-4 border-b border-[#081B3E]/15">
        <p className="text-xs font-bold uppercase tracking-[0.16em] text-[#1648EE]">
          {label}
        </p>

        <span className="text-xs font-bold tracking-widest text-[#F72585]">
          {String(index).padStart(2, "0")}
        </span>
      </div>

      <h3
        style={DISPLAY_STYLE}
        className="sab-card-title font-black uppercase tracking-[-0.025em]"
      >
        {title}
      </h3>

      <p className="sab-card-copy text-slate-600">
        {description}
      </p>

      <div className="sab-card-rule mt-auto">
        <span
          aria-hidden="true"
          className="block h-1 w-12 rounded-full bg-[#F72585]"
        />
      </div>
    </article>
  );
}

/* ======================================================
   ABOUT SCREEN
====================================================== */

const AboutScreen = ({
  language: controlledLanguage,
} = {}) => {
  const language = useAboutLanguage(controlledLanguage);
  const t = CONTENT[language];

  // Evita incorporar UTMs o fragmentos al canonical.
  const canonical =
    typeof window !== "undefined"
      ? `${window.location.origin}${window.location.pathname}`
      : "";

  return (
    <div
      lang={language}
      className="solcont-about bg-[#F7F3E9] text-[#081B3E]"
    >
      <style>{RESPONSIVE_STYLES}</style>

      {/* SEO TRADUCIDO */}

      <Helmet>
        <title>{t.seo.title}</title>

        <meta
          name="description"
          content={t.seo.description}
        />

        {canonical && (
          <link rel="canonical" href={canonical} />
        )}

        <meta
          property="og:title"
          content={t.seo.title}
        />

        <meta
          property="og:description"
          content={t.seo.description}
        />

        <meta
          property="og:type"
          content="website"
        />

        <meta
          property="og:site_name"
          content="SolCont México"
        />

        <meta
          property="og:locale"
          content={OG_LOCALES[language]}
        />

        {canonical && (
          <meta property="og:url" content={canonical} />
        )}
      </Helmet>

      {/* ==================================================
         HERO
      ================================================== */}

      <section
        className="relative isolate overflow-hidden text-[#F7F3E9]"
        style={GRID_STYLE}
      >
        <BrandOrbits />

        <div className="sab-container sab-split sab-hero relative z-10 grid items-center">
          <div className="min-w-0">
            <Eyebrow light>{t.hero.eyebrow}</Eyebrow>
            <AccentRule />

            <h1
              style={DISPLAY_STYLE}
              className="sab-hero-title font-black uppercase tracking-[-0.04em]"
            >
              {t.hero.title.map((line) => (
                <span key={line} className="block">
                  {line}
                </span>
              ))}
            </h1>

            <p className="sab-copy max-w-xl text-white/85">
              {t.hero.description}
            </p>
          </div>

          <aside
            aria-label={t.hero.cardLabel}
            className="sab-panel min-w-0 rounded-3xl bg-[#F7F3E9] text-[#081B3E] shadow-2xl shadow-blue-950/20"
          >
            <p className="text-[11px] font-bold uppercase leading-6 tracking-[0.15em] text-[#1648EE]">
              {t.hero.cardLabel}
            </p>

            <div className="sab-panel-list">
              {t.hero.pillars.map((pillar, index) => {
                const Icon = PILLAR_ICONS[index];

                return (
                  <div
                    key={index}
                    className="sab-pillar flex items-center border-t border-[#081B3E]/15"
                  >
                    <span className="sab-pillar-icon flex shrink-0 items-center justify-center rounded-xl bg-[#1648EE]/10">
                      <Icon
                        aria-hidden="true"
                        className="h-5 w-5 text-[#1648EE]"
                        strokeWidth={1.7}
                      />
                    </span>

                    <p
                      style={DISPLAY_STYLE}
                      className="sab-pillar-title min-w-0 font-black uppercase tracking-tight"
                    >
                      {pillar}
                    </p>
                  </div>
                );
              })}
            </div>

            <div className="sab-panel-note rounded-xl bg-[#081B3E]">
              <span
                aria-hidden="true"
                className="mb-3 block h-1 w-10 rounded-full bg-[#F72585]"
              />

              <p className="text-sm font-semibold leading-6 text-white">
                {t.hero.cardNote}
              </p>
            </div>
          </aside>
        </div>
      </section>

      {/* ==================================================
         NUESTRA ESENCIA
      ================================================== */}

      <section
        id="esencia"
        className="sab-container sab-section sab-anchor"
      >
        <div className="sab-split sab-essence grid items-center">
          <div className="min-w-0">
            <Eyebrow>{t.essence.eyebrow}</Eyebrow>
            <AccentRule />

            <h2
              style={DISPLAY_STYLE}
              className="sab-title sab-spaced font-black uppercase tracking-[-0.035em]"
            >
              {t.essence.title}
            </h2>

            <div className="sab-paragraphs">
              {t.essence.paragraphs.map(
                (paragraph, index) => (
                  <p
                    key={index}
                    className="text-slate-600"
                  >
                    {paragraph}
                  </p>
                )
              )}
            </div>

            <a
              href="/servicios"
              className="sab-button sab-spaced inline-flex items-center justify-center gap-3 rounded-xl bg-[#1648EE] text-sm font-bold text-white transition duration-200 hover:-translate-y-0.5 hover:bg-[#103BC9] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#F72585] focus-visible:ring-offset-4 focus-visible:ring-offset-[#F7F3E9] motion-reduce:transform-none motion-reduce:transition-none"
            >
              <span>{t.essence.cta}</span>

              <ArrowUpRight
                aria-hidden="true"
                className="h-4 w-4 shrink-0"
              />
            </a>
          </div>

          <TeamImage copy={t.essence} />
        </div>
      </section>

      {/* ==================================================
         MISIÓN Y VISIÓN
      ================================================== */}

      <section
        id="proposito"
        className="sab-anchor relative isolate overflow-hidden text-white"
        style={GRID_STYLE}
      >
        <BrandOrbits />

        <div className="sab-container sab-section relative z-10">
          <div className="sab-heading grid">
            <div>
              <Eyebrow light>
                {t.purpose.eyebrow}
              </Eyebrow>

              <AccentRule />
            </div>

            <h2
              style={DISPLAY_STYLE}
              className="sab-title max-w-3xl font-black uppercase tracking-[-0.035em]"
            >
              {t.purpose.title}
            </h2>
          </div>

          <div className="sab-cards grid md:grid-cols-2">
            {t.purpose.items.map((item, index) => (
              <PurposeCard
                key={index}
                index={index + 1}
                {...item}
              />
            ))}
          </div>
        </div>
      </section>

      {/* ==================================================
         SOLCONT DIGITAL
      ================================================== */}

      <section
        id="solcont-digital"
        className="sab-section sab-anchor bg-[#F7F3E9]"
      >
        <div className="sab-container">
          <div className="sab-heading grid">
            <div>
              <Eyebrow>{t.digital.eyebrow}</Eyebrow>
              <AccentRule />
            </div>

            <div className="min-w-0">
              <h2
                style={DISPLAY_STYLE}
                className="sab-title max-w-3xl font-black uppercase tracking-[-0.035em]"
              >
                {t.digital.title}
              </h2>

              <p className="sab-copy max-w-2xl text-slate-600">
                {t.digital.description}
              </p>
            </div>
          </div>
        </div>

        <div className="sab-digital-content">
          {/*
            Conservamos el componente existente.

            Recibe el idioma para su próxima actualización.
            No usamos key={language}, evitando reiniciar
            su estado cuando cambie el idioma.
          */}
          <SolContDigital language={language} />
        </div>
      </section>
    </div>
  );
};

export default AboutScreen;